import { useState, useEffect } from "react";
import { Shield, ShieldCheck, Clock, XCircle, History } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { Chip, DocButton } from "@/components/common";
import { useList, post, Table, PageTitle } from "@/pages/admin/kit";

const FILTERS = [
  ["", "Alle"], ["pending", "Stufe-1-Prüfung"], ["premium_pending", "Premium-Anträge"],
  ["verified", "Freigegeben Stufe 1"], ["premium", "Premium"], ["rejected", "Abgelehnt"],
  ["expired", "Abgelaufen"], ["withdrawn", "Zurückgezogen"],
];
const COV = [
  ["paid_processing", "Entgeltliche Wäschebearbeitung"], ["wash_dry", "Waschen & Trocknen"], ["ironing", "Bügeln"],
  ["processing_damage", "Bearbeitungsschäden"], ["custody_damage", "Obhutsschäden"], ["loss", "Verlust"], ["transport", "Abholung/Transport"],
];
const TONE = { none: "neutral", pending: "warn", rejected: "error", verified: "mint", premium_pending: "warn", premium: "mint", expired: "error", withdrawn: "error" };

function Detail({ pid, onChange }) {
  const [d, setD] = useState(null);
  const [cov, setCov] = useState({});
  const [exclusions, setExclusions] = useState("");
  const [validTo, setValidTo] = useState("");
  const load = () => api.get(`/admin/insurance-reviews/${pid}`).then((r) => {
    setD(r.data);
    const c = r.data.confirmation || {};
    setCov(Object.fromEntries(COV.map(([k]) => [k, !!c[`${k}_confirmed`]])));
    setExclusions(c.exclusions || ""); setValidTo(c.valid_to || "");
  });
  useEffect(() => { load(); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <p className="w-muted p-4">Lädt…</p>;
  const after = () => { load(); onChange(); };
  const act = (path, body, msg) => post(`/admin/insurance-reviews/${pid}/${path}`, body, msg, after);
  const reason = (label) => window.prompt(label) || "";
  const p = d.provider, pol = d.policy, conf = d.confirmation;
  return (
    <div className="space-y-5" data-testid="insurance-detail">
      <div className="flex items-center gap-2 flex-wrap">
        <strong>{p.display_name}</strong>
        <Chip tone={TONE[p.insurance_status] || "neutral"}>{p.status_label}</Chip>
        {p.insurance_next_review && <span className="w-muted text-sm">Nächste Prüfung: {p.insurance_next_review}</span>}
      </div>

      {/* Stufe 1 */}
      <div className="w-card p-4 space-y-2">
        <h4 className="font-bold flex items-center gap-2"><Shield size={16} aria-hidden="true" /> Haftpflicht-Nachweis (Stufe 1)</h4>
        {pol ? (
          <>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div><span className="w-muted">Versicherer:</span> {pol.insurer || "–"}</div>
              <div><span className="w-muted">Versicherungsnehmer:</span> {pol.policyholder || "–"}</div>
              <div><span className="w-muted">Vertragsbeginn:</span> {pol.start_date || "–"}</div>
              <div><span className="w-muted">Vertragsende:</span> {pol.end_date || "–"}</div>
              <div><span className="w-muted">Hochgeladen:</span> {fmtDateTime(pol.uploaded_at)}</div>
              <div><span className="w-muted">Status:</span> {pol.status}</div>
            </div>
            <DocButton fileId={pol.file_id} label="Versicherungsdokument ansehen" testId={`ins-doc-${pid}`} />
            {pol.reject_reason && <p className="text-sm text-[var(--w-error)]">Grund: {pol.reject_reason}</p>}
            {pol.status === "pending" && (
              <div className="flex flex-wrap gap-2 pt-1">
                <button className="w-btn w-btn-primary !min-h-[36px] !px-3" onClick={() => act("policy", { decision: "approve" }, "Stufe 1 freigegeben")} data-testid={`ins-policy-approve-${pid}`}>Bestätigen</button>
                <button className="w-btn w-btn-secondary !min-h-[36px] !px-3" onClick={() => act("policy", { decision: "reject", reason: reason("Ablehnungsgrund:") }, "Abgelehnt")} data-testid={`ins-policy-reject-${pid}`}>Ablehnen</button>
                <button className="w-btn w-btn-secondary !min-h-[36px] !px-3" onClick={() => act("policy", { decision: "request_more", reason: reason("Welche Unterlagen fehlen?") }, "Rückfrage gesendet")} data-testid={`ins-policy-more-${pid}`}>Weitere Infos anfordern</button>
              </div>
            )}
          </>
        ) : <p className="w-muted text-sm">Noch kein Nachweis eingereicht.</p>}
      </div>

      {/* Stufe 2 */}
      {conf && (
        <div className="w-card p-4 space-y-3">
          <h4 className="font-bold flex items-center gap-2"><ShieldCheck size={16} aria-hidden="true" /> Versichererbestätigung (Premium)</h4>
          <div className="text-sm"><span className="w-muted">Status:</span> {conf.decision} · <span className="w-muted">Aussteller:</span> {conf.insurer || "–"}</div>
          <DocButton fileId={conf.file_id} label="Bestätigung ansehen" testId={`ins-conf-doc-${pid}`} />
          <div>
            <p className="w-label mb-1">Bestätigte Leistungsbereiche</p>
            <div className="grid sm:grid-cols-2 gap-1">
              {COV.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!cov[k]} onChange={(e) => setCov({ ...cov, [k]: e.target.checked })} data-testid={`ins-cov-${k}-${pid}`} /> {l}</label>
              ))}
            </div>
          </div>
          <label className="block"><span className="w-label">Relevante Ausschlüsse</span><input className="w-input" value={exclusions} onChange={(e) => setExclusions(e.target.value)} data-testid={`ins-exclusions-${pid}`} /></label>
          <label className="block max-w-xs"><span className="w-label">Gültig bis (nächste Prüfung)</span><input type="date" className="w-input" value={validTo || ""} onChange={(e) => setValidTo(e.target.value)} data-testid={`ins-validto-${pid}`} /></label>
          <p className="w-muted text-xs">Premium erfordert die ausdrückliche Bestätigung der entgeltlichen Wäschebearbeitung. Nicht bestätigte Leistungen werden als „nicht bestätigt" angezeigt.</p>
          <div className="flex flex-wrap gap-2">
            <button className="w-btn w-btn-primary !min-h-[36px] !px-3" onClick={() => act("premium", { decision: "approve", exclusions, valid_to: validTo, ...cov }, "Premium erteilt")} data-testid={`ins-premium-approve-${pid}`}>Premium erteilen</button>
            <button className="w-btn w-btn-secondary !min-h-[36px] !px-3" onClick={() => act("premium", { decision: "reject", reason: reason("Ablehnungsgrund:") }, "Premium abgelehnt")} data-testid={`ins-premium-reject-${pid}`}>Ablehnen</button>
            {p.insurance_status === "premium" && <button className="w-btn w-btn-secondary !min-h-[36px] !px-3" onClick={() => act("premium", { decision: "revoke", reason: reason("Grund für Entzug:") }, "Premium entzogen")} data-testid={`ins-premium-revoke-${pid}`}>Premium entziehen</button>}
          </div>
        </div>
      )}

      {/* Statusaktionen */}
      <div className="flex flex-wrap gap-2">
        <button className="w-btn w-btn-secondary !min-h-[36px] !px-3" onClick={() => act("status", { action: "withdraw", reason: reason("Grund:") }, "Status zurückgezogen")} data-testid={`ins-withdraw-${pid}`}>Status zurückziehen</button>
        <button className="w-btn w-btn-secondary !min-h-[36px] !px-3" onClick={() => act("status", { action: "unclear", reason: reason("Grund:") }, "Als ungeklärt markiert")} data-testid={`ins-unclear-${pid}`}>Als ungeklärt markieren</button>
        <button className="w-btn w-btn-secondary !min-h-[36px] !px-3" onClick={() => act("status", { action: "block_bookings", reason: "" }, "Buchungen gesperrt")} data-testid={`ins-block-${pid}`}>Buchungen sperren</button>
        <button className="w-btn w-btn-secondary !min-h-[36px] !px-3" onClick={() => act("status", { action: "unblock_bookings", reason: "" }, "Buchungssperre aufgehoben")} data-testid={`ins-unblock-${pid}`}>Sperre aufheben</button>
      </div>

      {/* Historie */}
      {d.history?.length > 0 && (
        <div className="w-card p-4">
          <h4 className="font-bold flex items-center gap-2 mb-2"><History size={16} aria-hidden="true" /> Prüfverlauf</h4>
          <ul className="space-y-1 text-sm">
            {d.history.map((h) => (
              <li key={h.id} className="w-muted"><span className="text-ink">{h.prev_status} → {h.new_status}</span> · {h.reason} · {h.admin_email} · {fmtDateTime(h.created_at)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function InsuranceReviews() {
  const [filter, setFilter] = useState("");
  const { data, reload } = useList(`/admin/insurance-reviews${filter ? `?status=${filter}` : ""}`);
  const [open, setOpen] = useState(null);
  return (
    <div>
      <PageTitle sub="Prüfung der Haftpflicht-Nachweise (Stufe 1) und Versichererbestätigungen (Premium). Keine automatische Schadenregulierung.">Versicherungsprüfungen</PageTitle>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="insurance-filters">
        {FILTERS.map(([v, l]) => (
          <button key={v} className={`w-chip ${filter === v ? "bg-mint-ink text-white" : "bg-gray-100"}`} onClick={() => setFilter(v)} data-testid={`ins-filter-${v || "all"}`}>{l}</button>
        ))}
      </div>
      <Table testId="admin-insurance-reviews-table" empty="Keine Versicherungsnachweise" rows={data} cols={[
        ["Waschheld", (r) => <strong>{r.display_name}</strong>],
        ["Status", (r) => <Chip tone={TONE[r.insurance_status] || "neutral"}>{r.status_label}</Chip>],
        ["Nächste Prüfung", (r) => <span className="text-sm w-muted">{r.insurance_next_review || "–"}</span>],
        ["", (r) => <button className="w-btn w-btn-secondary !min-h-[34px] !px-3" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`ins-open-${r.id}`}>{open === r.id ? "Schließen" : "Prüfen"}</button>],
      ]} />
      {open && <div className="mt-4"><Detail pid={open} onChange={reload} /></div>}
    </div>
  );
}
